import { useEffect, useMemo, useReducer, useState } from 'react'
import { Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { Chart as ChartJS, Tooltip, Legend, LinearScale, CategoryScale, BarElement } from 'chart.js'
import { useTheme } from '@emotion/react'
import { DatePicker } from '@mui/x-date-pickers'
import { useLocalizationContext } from '@mui/x-date-pickers/internals'
import { Bar } from 'react-chartjs-2'
import Annotation from 'chartjs-plugin-annotation'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { apiGet, apiPost, apiPut } from '../generic/Api_Functions'
import { SmartSelect } from '../generic/smartSelect'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import createDialogTitle from '../generic/utilities/dialogUtil'
import { SaveRounded, Sort } from '@mui/icons-material'
import AlertUI from '../generic/AlertUI'
import { useToggle } from '../generic/hooks/useToggle'
import ArrangeSequence from './ArrangeSequencer'
import { DefaultAccordion } from '../generic/DefaultAccordion'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { useDateFormatter } from '../generic/hooks/useDateFormatter'

// Import typedefs from another file
/**
 * @typedef {import('../generic/utilities/objectStructures/sequencer/sequencer').Sequencer} Sequencer Sequencer object
 * @typedef {import('../generic/utilities/objectStructures/sequencer/sequencer').FabricationOrderSequencer} FabricationOrderSequencer FabricationOrder object
 * @typedef {import('../generic/utilities/objectStructures/globalStructures').IdCodeName} IdCodeName IdCodeName object
*/

const Sequencer = () => {
  const addDaysToDate = (date, days) => {
    const newDate = new Date(date.getTime())
    newDate.setDate(newDate.getDate() + days)
    return newDate
  }

  const onDispatchDates = (state, action) => {
    const newState = { ...state }
    if (action.type === 'set_start_date') {
      newState.startDate = action.value
      // Check if is at least a day
      if (addDaysToDate(newState.startDate, 1) > newState.endDate) {
        newState.endDate = addDaysToDate(newState.startDate, 1)
      }
    }
    if (action.type === 'set_end_date') {
      newState.endDate = action.value
      // Check if is at least a day
      if (addDaysToDate(newState.startDate, 1) > newState.endDate) {
        newState.startDate = addDaysToDate(newState.endDate, -1)
      }
    }
    if (action.type === 'set_time_group') {
      newState.timeGroup = action.value
      // Set appropiate range depending on group
      if (newState.timeGroup === 'DAY') {
        newState.endDate = addDaysToDate(newState.startDate, 6)
      } else if (newState.timeGroup === 'WEEK') {
        newState.endDate = addDaysToDate(newState.startDate, 28)
      } else if (newState.timeGroup === 'MONTH') {
        newState.endDate = addDaysToDate(newState.startDate, 365)
      } else if (newState.timeGroup === 'YEAR') {
        newState.endDate = addDaysToDate(newState.startDate, 365 * 4)
      }
    }
    return newState
  }

  /** @type {[Sequencer[], React.Dispatch<SetStateAction<Sequencer[]>>]} */
  const [sequencerData, setSequencerData] = useState(null)
  /** @type {[IdCodeName[], React.Dispatch<SetStateAction<IdCodeName[]>>]} */
  const [devices, setDevices] = useState([])
  /** @type {[IdCodeName, React.Dispatch<SetStateAction<IdCodeName>>]} */
  const [selectedDevice, setSelectedDevice] = useState(null)
  const theme = useTheme()
  const [dataLoading, setDataLoading] = useState(false)
  const [viewMode, setViewMode] = useState('Asset')
  const [dates, dispatchDates] = useReducer(onDispatchDates, { startDate: new Date(), endDate: addDaysToDate(new Date(), 6), timeGroup: 'DAY' })
  const { t_ } = useSmartTranslation()
  const { format } = useDateFormatter()
  const [openNewSequenceDialog, setOpenNewSequenceDialog] = useState(false)
  const [AlertElement, showAlert] = AlertUI()
  const [forceLoad, toggle] = useToggle()
  const [openArrangeDialog, setOpenArrangeDialog] = useState(false)
  const [arrangeSelectedAsset, setArrangeSelectedAsset] = useState('')
  const [arrangeSequenceData, setArrangeSequenceData] = useState([])

  ChartJS.register(
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    BarElement,
    Annotation
  )

  useEffect(() => {
    const loadDevices = async () => {
      setDevices(await apiGet('devicesBasicData'))
    }
    loadDevices()
  }, [])

  useEffect(() => {
    const loadSequence = async () => {
      setDataLoading(true)
      let data
      if (viewMode === 'Time') {
        data = await apiPost('fabricationOrderSequencer/device/' + selectedDevice.name, dates)
      } else {
        data = await apiGet('fabricationOrderSequencer/all')
      }
      setSequencerData(data)
      setDataLoading(false)
    }
    if (!selectedDevice) {
      return
    }

    loadSequence()
  }, [dates, selectedDevice, viewMode, forceLoad])

  // Set first device by default
  if (!selectedDevice && devices.length > 0) {
    setSelectedDevice(devices[0])
  }

  const textColor = theme.palette.text.primary

  const options = !sequencerData || sequencerData.length === 0
    ? null
    : {
        responsive: true,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            stacked: true,
            ticks: { color: textColor },
            grid: { color: textColor }
          },
          y: {
            stacked: true,
            ticks: {
              callback: (value, index, ticks) => {
                return value + ' H'
              },
              color: textColor
            },
            grid: { color: textColor }
          }
        },
        onClick: (event, element) => {
          if (viewMode === 'Time' || element[0]?.index === undefined || element[0]?.datasetIndex === undefined) {
            return
          }

          setArrangeSequenceData(structuredClone(sequencerData))
          setArrangeSelectedAsset(sequencerData[element[0]?.index].assetName)
          setOpenArrangeDialog(true)
        }
      }

  const localizationContext = useLocalizationContext()

  const labels = useMemo(() => {
    if (!sequencerData) {
      return []
    }

    if (viewMode === 'Time') {
      return sequencerData.map((item) => {
        const endDate = new Date(item.startDate)
        switch (dates.timeGroup) {
          case 'DAY':
            return localizationContext.utils.format(endDate, 'normalDateWithWeekday')
          case 'YEAR':
            return localizationContext.utils.format(endDate, 'year')
          case 'MONTH':
            return localizationContext.utils.format(endDate, 'month')
          case 'WEEK':
            return localizationContext.utils.format(endDate, 'normalDateWithWeekday') + ' - ' + localizationContext.utils.format(new Date(item.endDate), 'normalDateWithWeekday')
          default:
            return item.endDate
        }
      })
    }

    return sequencerData.map((item) => item.assetName)
  }, [sequencerData, dates.timeGroup, localizationContext.utils, viewMode])

  const data = useMemo(() => {
    if (!sequencerData) {
      return { labels: [], datasets: [] }
    }

    /** @type {FabricationOrderSequencer[]} */
    let fabricationOrders = []

    sequencerData.forEach(sequencer => {
      sequencer.fabricationOrderSequencerList.forEach(fabricationOrder => {
        fabricationOrders.push(fabricationOrder)
      })
    })

    // Order by order field
    fabricationOrders = fabricationOrders.sort((entry1, entry2) => {
      return entry1.order > entry2.order ? -1 : 1
    })

    // Create datasets
    const datasets = fabricationOrders.map((fabricationOrder) => {
      return {
        fabricationOrder,
        label: fabricationOrder.name + ' - ' + fabricationOrder.jobReference.name,
        data: sequencerData.map((item) => {
          let value = 0
          const fo = item.fabricationOrderSequencerList.find(fo1 => fo1.id === fabricationOrder.id && fo1.jobReference.id === fabricationOrder.jobReference.id)
          if (fo) {
            value = fo.duration
          }
          return value
        }),
        backgroundColor: fabricationOrder.color,
        borderColor: theme.palette.text.primary,
        borderWidth: 2,
        stack: 'Stack0'
      }
    })

    return {
      labels,
      datasets
    }
  }, [sequencerData, theme.palette, labels])

  const getTimeRangeSelector = () => {
    const views = dates.timeGroup === 'YEAR' ? ['year'] : dates.timeGroup === 'MONTH' ? ['year', 'month'] : ['year', 'month', 'day']

    const datePickerSx = {
      minWidth: '11rem',
      backgroundColor: theme.palette.background.paper,
      borderRadius: 1,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.text.secondary
      }
    }

    return (
      <>
        <DatePicker
          label={t_('Start')}
          views={views}
          format={format.date}
          onChange={(newDate) => {
            dispatchDates({ type: 'set_start_date', value: newDate })
          }}
          value={dates.startDate}
          disabled={dataLoading}
          sx={datePickerSx}
        />
        <DatePicker
          label={t_('End')}
          views={views}
          format={format.date}
          onChange={(newDate) => {
            dispatchDates({ type: 'set_end_date', value: newDate })
          }}
          value={dates.endDate}
          disabled={dataLoading}
          sx={datePickerSx}
        />
      </>
    )
  }

  const createNewSequence = async () => {
    await apiPut('fabricationOrderSequencer')
      .then(() => {
        showAlert({ message: t_('Created correctly.'), severity: 'success' })
        toggle()
      })
      .catch((e) => {
        showAlert({ title: t_('Error creating sequence'), message: e.message, severity: e.name })
      })
    setOpenNewSequenceDialog(false)
  }

  const onSortChanged = (sortedFabricationOrders) => {
    const newArrangeSequenceData = [...arrangeSequenceData]
    const index = newArrangeSequenceData.findIndex(data => data.assetName === arrangeSelectedAsset)
    newArrangeSequenceData[index].fabricationOrderSequencerList = sortedFabricationOrders
    setArrangeSequenceData(newArrangeSequenceData)
  }

  const onSelectedAssetChange = (asset) => {
    setArrangeSelectedAsset(asset)
  }

  const columns = [
    {
      name: t_('Asset'),
      field: 'assetName'
    },
    {
      name: t_('Order'),
      field: 'order'
    },
    {
      name: t_('Fabrication Order'),
      field: 'fabricationOrderName'
    },
    {
      name: t_('Part Reference'),
      field: 'partReferenceName'
    },
    {
      name: t_('Job Reference'),
      field: 'jobReferenceName'
    },
    {
      name: t_('Delivery date'),
      field: 'endDate',
      type: 'datetime'
    },
    {
      name: t_('Duration'),
      field: 'duration'
    }
  ]

  const dataFetch = () => {
    const data = []
    sequencerData?.forEach((sequencer) => {
      sequencer.fabricationOrderSequencerList.forEach((fabricationOrder) => {
        let duration = Number(fabricationOrder.duration)
        let durationString = Number(fabricationOrder.duration).toString()
        if (Math.round(duration) !== duration) {
          duration = Number(fabricationOrder.duration).toFixed(4)
          durationString = duration.toString()
          durationString = durationString.replace(/(\.\d*[1-9])0+$/, '$1')
        }
        data.push({
          assetName: fabricationOrder.assetName,
          fabricationOrderName: fabricationOrder.name,
          partReferenceName: fabricationOrder.partReferenceName,
          jobReferenceName: fabricationOrder.jobReference.name,
          endDate: fabricationOrder.endDate,
          duration: durationString + 'h',
          order: fabricationOrder.order
        })
      })
    })
    return data
  }

  const sortOptions = [
    {
      field: 'assetName',
      direction: 1
    },
    {
      field: 'order',
      direction: 1
    }
  ]

  const onChangeSequencerList = (newSequenceList) => {
    setArrangeSequenceData(newSequenceList)
  }

  const saveArrange = async () => {
    await apiPost('fabricationOrderSequencer', arrangeSequenceData)
      .then(() => {
        showAlert({ message: t_('Updated correctly.'), severity: 'success' })
        toggle()
      })
      .catch((e) => {
        showAlert({ title: t_('Error updating sequence'), message: e.message, severity: e.name })
      })
    setOpenArrangeDialog(false)
  }

  return (
    <>
      {AlertElement}
      <SmartDialog
        title={createDialogTitle({ title: t_('Arrange Fabrication orders'), icon: Sort })}
        setOpen={openArrangeDialog}
        renderComponent={<ArrangeSequence sequencerList={arrangeSequenceData} onChangeSequencerList={onChangeSequencerList} selectedAsset={arrangeSelectedAsset} onSortChanged={onSortChanged} onSelectedAssetChange={onSelectedAssetChange} />}
        acceptCallback={() => { saveArrange() }}
        cancelCallback={() => setOpenArrangeDialog(false)}
      />
      <SmartDialog
        title={createDialogTitle({ title: t_('Update sequence?'), icon: SaveRounded })}
        setOpen={openNewSequenceDialog}
        renderComponent={<Typography>{t_('Are you sure you want to update the sequence?')}</Typography>}
        accept={false}
        cancelCallback={() => setOpenNewSequenceDialog(false)}
        renderCustomButtons={
          <Button
            color='primary'
            onClick={() => { createNewSequence() }}
            variant='outlined'
          >
            <Typography color='primary'>{t_('Update')}</Typography>
            <SaveRounded color='primary' sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
          </Button>
        }
      />
      <Stack gap={3} sx={{ fontSize: '1.4em', padding: '1rem', marginBottom: '1rem', position: 'sticky', zIndex: '1', top: '0rem', backgroundColor: '#303030' }}>
        <Stack direction='row' alignItems='flex-end' justifyContent='space-between'>
          <Stack direction='row' gap={3}>
            <SmartSelect
              label={t_('Select option')}
              selectableOptions={[t_('Time'), t_('Asset')]}
              onChange={(item) => item === t_('Time') ? setViewMode('Time') : setViewMode('Asset')}
              value={(t_(viewMode))}
              minWidth='7rem'
            />
            {viewMode === 'Time'
              ? (
                <>
                  <SmartSelect
                    label={t_('Asset')}
                    selectableOptions={devices.map((item) => item.name)}
                    disabled={dataLoading}
                    value={selectedDevice?.name}
                    onChange={(item) => setSelectedDevice(devices.find((device) => device.name === item))}
                    minWidth='15rem'
                  />
                  <ToggleButtonGroup disabled={dataLoading} exclusive value={dates.timeGroup} onChange={(event) => dispatchDates({ type: 'set_time_group', value: event.target.value })} size='small'>
                    <ToggleButton value='DAY'>{t_('Day')}</ToggleButton>
                    <ToggleButton value='WEEK'>{t_('Week')}</ToggleButton>
                    <ToggleButton value='MONTH'>{t_('Month')}</ToggleButton>
                    <ToggleButton value='YEAR'>{t_('Year')}</ToggleButton>
                  </ToggleButtonGroup>

                  {getTimeRangeSelector()}
                </>
                )
              : null}
          </Stack>
          <Button
            color='primary'
            variant='outlined'
            sx={{ height: '3.5rem' }}
            onClick={() => setOpenNewSequenceDialog(true)}
          >
            <Typography>{t_('Update Sequence')}</Typography>
          </Button>
        </Stack>
      </Stack>
      <DefaultAccordion
        headerRender={t_('Graphic')}
        defaultExpanded={false}
      >
        <Bar options={options} data={data} />
      </DefaultAccordion>
      <DefaultAccordion
        headerRender={t_('Table')}
        defaultExpanded={false}
      >
        <SmartMateriaUITable
          columns={columns}
          dataFetch={dataFetch}
          sortOptions={sortOptions}
        />
      </DefaultAccordion>
    </>
  )
}

export default Sequencer
